import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { DirectoryMailDto, LogEntryDto, MentionSource } from "@klantpunt/shared";
import { IconMail } from "../../app/icons";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { MentionComposer } from "./MentionComposer";
import { MentionText } from "./MentionText";

export const mentionsKey = ["mentions"] as const;

const fmt = (iso: string) => new Date(iso).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" });

const names = (rs: DirectoryMailDto["to"]) => rs.map((r) => r.name ?? r.email).join(", ");

/** Mail in het logboek (adresboek): onderwerp en ontvangers, open te klikken voor de tekst. */
function MailEntry({ mail }: { mail: DirectoryMailDto }) {
  return (
    <details className="log-mail">
      <summary>
        <IconMail size={14} aria-hidden="true" />
        <span>
          <strong>{mail.subject}</strong>
          <span className="muted">
            {" "}
            · {mail.direction === "in" ? "van" : "aan"} {names(mail.to)}
            {mail.cc.length > 0 && `, cc ${names(mail.cc)}`}
          </span>
        </span>
      </summary>
      <div className="log-mail-body">
        {mail.building && <p className="muted">Over {mail.building.name}</p>}
        <p>{mail.body}</p>
      </div>
    </details>
  );
}

/** Regels van een logboek: opmerkingen met een accent, mails met een eigen punt, vaste regels grijs. */
export function LogList({
  entries,
}: {
  entries: {
    id: string;
    kind: string;
    body: string | null;
    user: { name: string } | null;
    createdAt: string;
    mail?: DirectoryMailDto;
  }[];
}) {
  return (
    <ul className="log">
      {entries.map((ev) => (
        <li key={ev.id} className={ev.kind === "comment" ? "comment" : ev.mail ? "mail" : "sys"}>
          <div className="who">
            <strong>{ev.user?.name ?? "Systeem"}</strong> · {fmt(ev.createdAt)}
            {ev.kind === "email_out" && " · mail verstuurd"}
            {ev.kind === "email_in" && " · mail ontvangen"}
          </div>
          {ev.mail ? (
            <MailEntry mail={ev.mail} />
          ) : (
            <div className="txt">{ev.body != null && <MentionText body={ev.body} />}</div>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Generiek logboek (issue #154) bij een entiteit met `log_entries`: lijst plus invoer met @-vermeldingen.
 * `path` is de API-route van het logboek (GET geeft de regels, POST `{ body }` voegt er een toe).
 */
export function Logbook({
  entity,
  id,
  path,
  buildingId,
  canComment = true,
  actions,
  className = "sec logbook",
}: {
  entity: MentionSource;
  id: string;
  path: string;
  /** Gebouw van het logboek: vaste leveranciers daarvan staan bovenaan bij @. */
  buildingId?: string;
  canComment?: boolean;
  /** Knoppen naast de kop, bijv. *Mail sturen* in het adresboek. */
  actions?: ReactNode;
  className?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["logbook", entity, id];
  const q = useQuery({ queryKey: key, queryFn: () => api<LogEntryDto[]>(path) });
  return (
    <section className={className} aria-label="Logboek">
      <div className="logbook-head">
        <p className="label">Logboek {q.data && <span className="num">{q.data.length}</span>}</p>
        {actions}
      </div>
      {canComment && (
        <MentionComposer
          id={`log-${id}`}
          entity={entity}
          targetId={id}
          buildingId={buildingId}
          onSubmit={async (body) => {
            try {
              qc.setQueryData(key, await api<LogEntryDto[]>(path, { method: "POST", body: { body } }));
              void qc.invalidateQueries({ queryKey: mentionsKey });
              return true;
            } catch (e) {
              toast(e instanceof ApiError ? e.message : "Opmerking plaatsen lukt niet");
              return false;
            }
          }}
        />
      )}
      {q.isPending && <Loading small />}
      {q.isError && <p className="hint warn">Het logboek kon niet worden geladen.</p>}
      {q.data && <LogList entries={q.data} />}
    </section>
  );
}
